import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, GridLegacy as Grid, Divider, Alert, Tab, Tabs, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, IconButton, TablePagination, FormHelperText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { onboardingService, userService } from '../services/api';
import { usePagination } from '../hooks/usePagination';
import { EMPLOYMENT_TYPE_META } from './internshipTypes';

const statusColor: any = { Draft: 'default', Submitted: 'primary', Approved: 'success', ChangesRequested: 'warning' };

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const OnboardingReview: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewReason, setReviewReason] = useState('');
  const [officeInfo, setOfficeInfo] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [officeInfoMsg, setOfficeInfoMsg] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [offerLetterUploading, setOfferLetterUploading] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);

  // Initiate onboarding state
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ fullName: '', email: '', role: 'Employee' });
  const [createdCredentials, setCreatedCredentials] = useState<any>(null);

  useEffect(() => { loadList(); }, []);

  const loadList = async () => {
    try {
      const data = await onboardingService.getAll();
      setList(data);
    } catch { }
    setLoading(false);
  };

  const openView = async (id: number) => {
    const data = await onboardingService.getById(id);
    setSelected(data);
    setOfficeInfo(data.officeInfo || {});
    setOfficeInfoMsg(null);
    setTab(0);
    setViewOpen(true);
  };

  const handleReview = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onboardingService.review(selected.id, { action: reviewAction, changeRequestReason: reviewReason });
      setMessage(reviewAction === 'Approve' ? 'Form approved.' : 'Changes requested.');
      setReviewOpen(false);
      setViewOpen(false);
      await loadList();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Action failed.');
    }
    setSaving(false);
  };

  const handleOfferLetterUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setOfferLetterUploading(true);
    setOfficeInfoMsg(null);
    try {
      const result = await onboardingService.uploadOfferLetter(selected.id, file);
      setOfficeInfo({ ...officeInfo, offerLetterUrl: result.url });
      setOfficeInfoMsg({ text: 'Offer letter uploaded successfully.', severity: 'success' });
    } catch {
      setOfficeInfoMsg({ text: 'Upload failed. Please try again.', severity: 'error' });
    }
    setOfferLetterUploading(false);
    e.target.value = '';
  };

  const handleHRDocUpload = (docType: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selected) return;
    setUploadingDocType(docType);
    for (const file of Array.from(files)) {
      try {
        const result = await onboardingService.uploadDocument(selected.id, docType, file);
        setSelected((prev: any) => ({
          ...prev,
          documentUploads: [...(prev.documentUploads || []), result],
        }));
      } catch { }
    }
    setUploadingDocType(null);
    e.target.value = '';
  };

  const handleHRDocDelete = async (docId: number) => {
    if (!selected) return;
    try {
      await onboardingService.deleteDocument(selected.id, docId);
      setSelected((prev: any) => ({
        ...prev,
        documentUploads: (prev.documentUploads || []).filter((d: any) => d.id !== docId),
      }));
    } catch { }
  };

  const handleSaveOfficeInfo = async () => {
    if (!selected) return;
    setSaving(true);
    setOfficeInfoMsg(null);
    try {
      const payload = {
        ...officeInfo,
        salary: officeInfo.salary !== '' && officeInfo.salary != null ? parseFloat(officeInfo.salary) : null,
      };
      const result = await onboardingService.saveOfficeInfo(selected.id, payload);
      // Marking someone Intern sets up their internship automatically — tell HR whether it
      // worked (it needs a reporting manager to act as mentor).
      const internshipMessage: string | null = result?.internshipMessage ?? null;
      setOfficeInfoMsg({
        text: internshipMessage ? `Office info saved. ${internshipMessage}` : 'Office info saved successfully.',
        severity: internshipMessage?.includes('NOT created') ? 'error' : 'success',
      });
    } catch (err: any) {
      setOfficeInfoMsg({ text: err?.response?.data?.message || 'Save failed. Please try again.', severity: 'error' });
    }
    setSaving(false);
  };

  const handleInitiate = async () => {
    if (!newEmployee.fullName || !newEmployee.email) return;
    setSaving(true);
    try {
      const result = await userService.createUser({
        fullName: newEmployee.fullName,
        email: newEmployee.email,
        role: newEmployee.role,
      });
      setCreatedCredentials(result);
      await loadList();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to create employee account.');
      setInitiateOpen(false);
    }
    setSaving(false);
  };

  const handleCloseInitiate = () => {
    setInitiateOpen(false);
    setCreatedCredentials(null);
    setNewEmployee({ fullName: '', email: '', role: 'Employee' });
  };

  // Called unconditionally (rules of hooks) even before the `loading` early return.
  const { pagedRows: pagedList, paginationProps: listPaginationProps } = usePagination(list);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Employee Onboarding — HR Review</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInitiateOpen(true)}>
          Initiate Onboarding
        </Button>
      </Box>
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Last Updated</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">No onboarding forms submitted yet.</TableCell></TableRow>
            )}
            {pagedList.map((item: any) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.userFullName}</TableCell>
                <TableCell>{item.userEmail}</TableCell>
                <TableCell><Chip label={item.status} color={statusColor[item.status] || 'default'} size="small" /></TableCell>
                <TableCell>{new Date(item.updatedAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openView(item.id)}>
                    {item.status === 'Submitted' ? 'Review' : 'View'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length > 0 && <TablePagination {...listPaginationProps} />}
      </TableContainer>

      {/* View / Review Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Onboarding Form — {selected?.userFullName}
            <Chip label={selected?.status} color={statusColor[selected?.status] || 'default'} size="small" sx={{ ml: 1 }} />
          </Box>
          {selected?.status === 'Submitted' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="success" size="small" startIcon={<CheckCircleIcon />}
                onClick={() => { setReviewAction('Approve'); setReviewReason(''); setReviewOpen(true); }}>
                Approve
              </Button>
              <Button variant="outlined" color="warning" size="small" startIcon={<EditNoteIcon />}
                onClick={() => { setReviewAction('RequestChanges'); setReviewReason(''); setReviewOpen(true); }}>
                Request Changes
              </Button>
            </Box>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Personal Details" />
            <Tab label="Experience" />
            <Tab label="Education" />
            <Tab label="Previous Company" />
            <Tab label="Documents" />
            <Tab label="Office Use (HR)" />
          </Tabs>

          {/* Tab 0 — Personal */}
          {tab === 0 && selected && (
            <Grid container spacing={2}>
              <Field label="Full Name" value={selected.userFullName} />
              <Field label="Email" value={selected.userEmail} />
              <Field label="Blood Group" value={selected.bloodGroup} />
              <Field label="Contact No." value={selected.contactNo} />
              <Field label="Alternate No." value={selected.alternateNo} />
              <Field label="Date of Birth" value={selected.dateOfBirth ? new Date(selected.dateOfBirth).toLocaleDateString() : null} />
              <Field label="Marital Status" value={selected.maritalStatus} />
              <Field label="PAN No." value={selected.panNo} />
              <Field label="Emergency Contact" value={selected.emergencyContact} />
              <Field label="Spouse Name" value={selected.spouseName} />
              <Field label="Children" value={selected.childrenCount} />
              <Grid item xs={12}><Field label="Current Address" value={selected.currentAddress} /></Grid>
              <Grid item xs={12}><Field label="Permanent Address" value={selected.permanentAddress} /></Grid>
              <Field label="Father's Name" value={selected.fatherName} />
              <Field label="Father's Contact" value={selected.fatherContact} />
              <Field label="Mother's Name" value={selected.motherName} />
              <Field label="Mother's Contact" value={selected.motherContact} />
              <Grid item xs={12}><Field label="Hobbies" value={selected.hobbies} /></Grid>
              <Grid item xs={12}><Field label="Previous Achievements" value={selected.previousAchievements} /></Grid>
              <Field label="UAN Number (EPF)" value={selected.uanNumber} />
              <Field label="EPF / PF Member ID" value={selected.epfNumber} />
            </Grid>
          )}

          {/* Tab 1 — Experience */}
          {tab === 1 && selected && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Field label="Total Years of Experience" value={selected.totalYearsExperience} />
                <Field label="Specialized Technology" value={selected.specializedTechnology} />
              </Grid>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Company</TableCell><TableCell>Tenure</TableCell>
                  <TableCell>HR Contact</TableCell><TableCell>HR Email</TableCell><TableCell>Reason</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(selected.experiences || []).map((e: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{e.companyName}</TableCell><TableCell>{e.tenure}</TableCell>
                      <TableCell>{e.hrContactNumber}</TableCell><TableCell>{e.hrMailId}</TableCell>
                      <TableCell>{e.reasonForChange}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Tab 2 — Education */}
          {tab === 2 && selected && (
            <Box>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Sr.</TableCell><TableCell>Course</TableCell><TableCell>Year</TableCell>
                  <TableCell>University</TableCell><TableCell>%/Marks</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(selected.educations || []).map((e: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{e.srNo}</TableCell><TableCell>{e.courseName}</TableCell>
                      <TableCell>{e.yearOfPassOut}</TableCell><TableCell>{e.universityName}</TableCell>
                      <TableCell>{e.percentageOrMarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Certifications</Typography>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Specialization</TableCell><TableCell>Year</TableCell>
                  <TableCell>Institute</TableCell><TableCell>Results</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(selected.certifications || []).map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.certificationSpecialization}</TableCell><TableCell>{c.yearOfPassOut}</TableCell>
                      <TableCell>{c.instituteName}</TableCell><TableCell>{c.results}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Tab 3 — Previous Company */}
          {tab === 3 && selected?.previousCompany && (
            <Grid container spacing={2}>
              <Field label="Company Name" value={selected.previousCompany.companyName} />
              <Field label="Employee Code" value={selected.previousCompany.employeeCode} />
              <Grid item xs={12}><Field label="Company Address" value={selected.previousCompany.companyAddress} /></Grid>
              <Field label="Landline" value={selected.previousCompany.landlineNumber} />
              <Field label="HR Email" value={selected.previousCompany.hrEmailId} />
              <Field label="From" value={selected.previousCompany.dateEmployedFrom} />
              <Field label="To" value={selected.previousCompany.dateEmployedTo} />
              <Field label="Designation" value={selected.previousCompany.designation} />
              <Field label="Salary" value={selected.previousCompany.salaryDetails} />
              <Field label="Supervisor" value={selected.previousCompany.supervisorName} />
              <Field label="Reason for Leaving" value={selected.previousCompany.reasonForLeaving} />
              <Field label="Employment Type" value={selected.previousCompany.employmentType} />
              <Grid item xs={12}><Field label="References" value={selected.previousCompany.references} /></Grid>
            </Grid>
          )}

          {/* Tab 4 — Documents */}
          {tab === 4 && (
            <Box>
              {selected?.documentChecklist && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Employee Checklist</Typography>
                  {[
                    ['offerLetter', 'Offer Letter'],
                    ['relievingLetter', 'Relieving Letter'],
                    ['paySlipBankStatement', 'Pay Slip / Bank Statements'],
                    ['aadharPanCard', 'Aadhar & PAN Card Photocopy'],
                    ['educationalDocuments', 'Educational Documents'],
                  ].map(([key, label]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={(selected.documentChecklist as any)[key] ? 'Submitted' : 'Not Submitted'}
                        color={(selected.documentChecklist as any)[key] ? 'success' : 'default'}
                        size="small"
                      />
                      <Typography variant="body2">{label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {[
                { type: 'ExperienceLetter',  label: 'Experience Letter' },
                { type: 'AppointmentLetter', label: 'Appointment Letter' },
                { type: 'SalarySlip',        label: 'Salary Slips (Last 3 Months)' },
                { type: 'Educational',       label: 'Educational Documents' },
                { type: 'Other',             label: 'Other Documents' },
              ].map(({ type, label }) => {
                const files = (selected?.documentUploads || []).filter((d: any) => d.documentType === type);
                return (
                  <Box key={type} sx={{ mb: 2, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">{label}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                        disabled={uploadingDocType === type}
                      >
                        {uploadingDocType === type ? 'Uploading…' : 'Upload'}
                        <input type="file" hidden multiple onChange={handleHRDocUpload(type)} />
                      </Button>
                    </Box>
                    {files.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No files uploaded.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {files.map((doc: any) => (
                          <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.fileName}</a>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(doc.uploadedAt).toLocaleDateString()}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => handleHRDocDelete(doc.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Tab 5 — Office Use (HR only) */}
          {tab === 5 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>This section is only visible to HR and Admin. Employees cannot see this.</Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Employee Code" value={officeInfo.employeeCode || ''} onChange={e => setOfficeInfo({ ...officeInfo, employeeCode: e.target.value })} size="small" helperText={officeInfo.employeeCode ? '' : 'Leave blank to auto-generate (e.g. EMP001)'} /></Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Offer Letter</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {officeInfo.offerLetterUrl && (
                      <Button size="small" variant="outlined" component="a" href={officeInfo.offerLetterUrl} target="_blank" rel="noopener noreferrer">View</Button>
                    )}
                    <Button size="small" variant="outlined" component="label" disabled={offerLetterUploading}>
                      {offerLetterUploading ? 'Uploading...' : officeInfo.offerLetterUrl ? 'Replace' : 'Upload PDF/DOC'}
                      <input type="file" hidden accept=".pdf,.doc,.docx" onChange={handleOfferLetterUpload} />
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Interview Taken By" value={officeInfo.interviewTakenBy || ''} onChange={e => setOfficeInfo({ ...officeInfo, interviewTakenBy: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Hired By" value={officeInfo.hiredBy || ''} onChange={e => setOfficeInfo({ ...officeInfo, hiredBy: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Salary" type="number" value={officeInfo.salary || ''} onChange={e => setOfficeInfo({ ...officeInfo, salary: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Designation" value={officeInfo.designation || ''} onChange={e => setOfficeInfo({ ...officeInfo, designation: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Joining Date" type="date" value={officeInfo.joiningDate ? officeInfo.joiningDate.split('T')[0] : ''} onChange={e => setOfficeInfo({ ...officeInfo, joiningDate: e.target.value })} size="small" InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Employment Type</InputLabel>
                    <Select value={officeInfo.employmentType || ''} onChange={e => setOfficeInfo({ ...officeInfo, employmentType: e.target.value })} label="Employment Type">
                      {Object.entries(EMPLOYMENT_TYPE_META).map(([value, meta]) => (
                        <MenuItem key={value} value={value}>{meta.label}</MenuItem>
                      ))}
                    </Select>
                    {officeInfo.employmentType === 'Intern' && (
                      <FormHelperText>On save, the internship (6 months, mentor = reporting manager) is created automatically.</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="UAN Number (EPF)" value={officeInfo.uanNumber || ''} onChange={e => setOfficeInfo({ ...officeInfo, uanNumber: e.target.value })} size="small" helperText="12-digit Universal Account Number" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="EPF / PF Member ID" value={officeInfo.epfNumber || ''} onChange={e => setOfficeInfo({ ...officeInfo, epfNumber: e.target.value })} size="small" helperText="From previous employer" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Remarks" value={officeInfo.remarks || ''} onChange={e => setOfficeInfo({ ...officeInfo, remarks: e.target.value })} size="small" multiline rows={3} /></Grid>
              </Grid>
              {officeInfoMsg && (
                <Alert severity={officeInfoMsg.severity} sx={{ mt: 2 }} onClose={() => setOfficeInfoMsg(null)}>
                  {officeInfoMsg.text}
                </Alert>
              )}
              <Button variant="contained" onClick={handleSaveOfficeInfo} sx={{ mt: 2 }} disabled={saving}>
                {saving ? 'Saving...' : 'Save Office Info'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Initiate Onboarding Dialog */}
      <Dialog open={initiateOpen} onClose={handleCloseInitiate} maxWidth="sm" fullWidth>
        <DialogTitle>Initiate Employee Onboarding</DialogTitle>
        <DialogContent>
          {!createdCredentials ? (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField fullWidth label="Full Name" value={newEmployee.fullName}
                onChange={e => setNewEmployee({ ...newEmployee, fullName: e.target.value })} required />
              <TextField fullWidth label="Email Address" type="email" value={newEmployee.email}
                onChange={e => setNewEmployee({ ...newEmployee, email: e.target.value })} required />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select value={newEmployee.role} onChange={e => setNewEmployee({ ...newEmployee, role: e.target.value })} label="Role">
                  <MenuItem value="Employee">Employee</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info">
                An account will be created and login credentials generated. Share them with the employee so they can log in and fill their onboarding form.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>Employee account created successfully!</Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share these credentials with the employee. They must log in and complete their onboarding form.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body1" fontWeight={600}>{createdCredentials.username}</Typography>
                  </Box>
                  <Button size="small" startIcon={<ContentCopyIcon />}
                    onClick={() => navigator.clipboard.writeText(createdCredentials.username)}>
                    Copy
                  </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Temporary Password</Typography>
                    <Typography variant="body1" fontWeight={600}>{createdCredentials.temporaryPassword}</Typography>
                  </Box>
                  <Button size="small" startIcon={<ContentCopyIcon />}
                    onClick={() => navigator.clipboard.writeText(createdCredentials.temporaryPassword)}>
                    Copy
                  </Button>
                </Box>
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Save these credentials now — the password will not be shown again.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInitiate}>{createdCredentials ? 'Done' : 'Cancel'}</Button>
          {!createdCredentials && (
            <Button variant="contained" onClick={handleInitiate} disabled={saving || !newEmployee.fullName || !newEmployee.email}>
              {saving ? 'Creating...' : 'Create Account'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve / Request Changes Dialog */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{reviewAction === 'Approve' ? 'Approve Form' : 'Request Changes'}</DialogTitle>
        <DialogContent>
          {reviewAction === 'RequestChanges' && (
            <TextField
              fullWidth label="Reason for requesting changes" multiline rows={3}
              value={reviewReason} onChange={e => setReviewReason(e.target.value)}
              sx={{ mt: 1 }} required
            />
          )}
          {reviewAction === 'Approve' && (
            <Typography>Are you sure you want to approve this onboarding form? The employee will not be able to make further changes.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button variant="contained" color={reviewAction === 'Approve' ? 'success' : 'warning'}
            onClick={handleReview} disabled={saving}>
            {saving ? 'Processing...' : reviewAction === 'Approve' ? 'Approve' : 'Request Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingReview;
